import { useState } from "react";

import { useParams } from "@tanstack/react-router";
import { Card, Flex, Spin, Typography } from "antd";

import { useAPI } from "@neu/libui";

import {
	Field,
	KeyValueRow,
	LabelValueCard,
	SnapshotHeader,
	StatsBar,
	TableCard,
} from "../patient/snapshotcomponents";

import { PopupForm } from "#components";
import { fmt, phone } from "#utils";

import styles from "./PlanSnapshot.module.scss";
import {
	carrierColumns,
	getAccountManagerRows,
	getContactRows,
	getFinancialRows,
	getHeaderMeta,
	getOverrideRows,
	getPlanDetailFields,
	getStatsData,
	pbmColumns,
} from "./planSnapshotData";

const { Text } = Typography;

export const PlanSnapshot = () => {
	const { planID } = useParams({ strict: false });
	const [{ data: plan, isLoading }] = useAPI(
		planID ? `/plan/snapshot?id=${planID}` : null
	);

	const planData = (plan ?? {}) as Record<string, any>;
	const vendorName = planData.vendor_id?.name;
	const contacts = getContactRows(planData);
	const carriers = Array.isArray(planData.sf_medical_carrier)
		? planData.sf_medical_carrier
		: [];
	const pbmHistory = Array.isArray(planData.pbm_history)
		? planData.pbm_history
		: [];

	const [openForm, setOpenForm] = useState(false);

	return (
		<Flex vertical className={styles.snapshot}>
			{(isLoading || !plan) && (
				<Flex
					flex={1}
					justify="center"
					align="center"
					className={styles.loadingWrap}
				>
					<Spin size="large" />
				</Flex>
			)}

			{!isLoading && plan && (
				<>
					<SnapshotHeader
						className={styles.header}
						avatar={
							<Flex className={styles.headerIcon}>
								<Text>📋</Text>
							</Flex>
						}
						title={fmt(planData.name, "Untitled Plan")}
						statusLabel={fmt(planData.status, "Unknown")}
						statusColor={
							planData.status === "Active"
								? "success"
								: planData.status === "Pending"
									? "warning"
									: "default"
						}
						infoClassName={styles.headerInfo}
						nameClassName={styles.headerName}
						metaClassName={styles.headerMeta}
						actionsClassName={styles.headerActions}
						meta={getHeaderMeta(planData)}
						actions={[
							{
								label: "Edit",
								type: "primary",
								onClick: () => {
									setOpenForm(true);
								},
							},
						]}
					/>
					<PopupForm
						open={openForm}
						entityName="plan"
						mode="edit"
						recordID={planData.id}
						onClose={() => {
							setOpenForm(false);
						}}
					/>
					<StatsBar
						className={styles.statsBar}
						cardClassName={styles.statCard}
						valueClassName={styles.statValue}
						labelClassName={styles.statLabel}
						stats={getStatsData(planData)}
					/>

					<Flex className={styles.mainGrid}>
						<Flex vertical className={styles.leftColumn}>
							<Card
								title="Plan Details"
								size="small"
								className={styles.card}
							>
								<Flex className={styles.detailsGrid}>
									{getPlanDetailFields(planData).map((f) => (
										<Field
											key={f.label}
											label={f.label}
											value={f.value}
											span={f.span}
										/>
									))}
								</Flex>
							</Card>

							<LabelValueCard
								title="Financials"
								className={styles.card}
								rowClassName={styles.financialRow}
								rows={getFinancialRows(planData)}
								bold
							/>

							<LabelValueCard
								title="Override Procedures"
								className={styles.card}
								variant="vertical"
								rows={getOverrideRows(planData)}
							/>
						</Flex>

						<Flex vertical className={styles.rightColumn}>
							<Card
								title="Account Management"
								size="small"
								className={styles.card}
							>
								{vendorName && (
									<Flex
										align="center"
										gap={12}
										className={styles.vendorRow}
									>
										<Text strong>{vendorName}</Text>
									</Flex>
								)}
								{getAccountManagerRows(planData).map((row) => (
									<KeyValueRow
										key={row.label}
										label={row.label}
										value={row.value}
										className={styles.contactItem}
									/>
								))}
							</Card>

							<Card
								title="Key Contacts"
								size="small"
								className={styles.card}
							>
								{contacts.length === 0 && (
									<Text type="secondary">No contacts</Text>
								)}
								{contacts.map(
									(
										c: Record<string, string>,
										idx: number
									) => (
										<Flex
											key={`${c.name}-${idx}`}
											vertical
											className={styles.contactItem}
										>
											<Text strong>{c.name}</Text>
											<Text type="secondary">
												{c.type} • {phone(c.phone)}
											</Text>
										</Flex>
									)
								)}
							</Card>
						</Flex>
					</Flex>

					<Flex className={styles.bottomGrid}>
						<TableCard
							title="Medical Carriers"
							className={styles.card}
							columns={carrierColumns}
							dataSource={carriers}
							rowKey="id"
							emptyText="No carriers"
						/>

						<TableCard
							title="PBM History"
							className={styles.card}
							columns={pbmColumns}
							dataSource={pbmHistory}
							rowKey="pbm"
							emptyText="No PBM history"
						/>
					</Flex>
				</>
			)}
		</Flex>
	);
};
